package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.ui.runtime.render.layer.StateSnapshot
import top.wcpe.allincore.ui.runtime.render.layer.TransformEntry

/**
 * 录制期渲染状态栈（变换 / 透明度 / 色彩矩阵 / 裁剪）的平台中立聚合。
 *
 * 从 LayerBufferingBridge 抽出的可单测独立件（ADR-0046 原计划的 StateStacks 拆分，
 * 随 ADR-0072 R2 迁 commonMain 一并完成）：LayerBufferingBridge 组合本类完成状态录制，
 * [snapshot] 产出的 [StateSnapshot] 挂在每条 DrawRecord 上供回放期还原状态。
 *
 * 语义与拆分前逐一一致：
 * - 裁剪层叠求交：新裁剪与栈顶（外层有效裁剪）求交后入栈，使 [snapshot] 取的栈顶始终是
 *   「外层 ∩ 本层」的有效裁剪（否则内层控件会用自身矩形顶替外层 scrollview 裁剪，文字逃逸）。
 * - 色彩矩阵入栈时防御性复制，避免外部数组后续被修改影响已录制快照。
 */
class StateStacks {

    private val transformStack = ArrayDeque<TransformEntry>()
    private val alphaStack = ArrayDeque<Float>()

    /** 色彩矩阵栈（E1 filter）：每项 4×5 共 20 元素，与 [alphaStack] 同构。 */
    private val colorMatrixStack = ArrayDeque<FloatArray>()
    private val clipStack = ArrayDeque<FloatArray>()

    /** 清空全部状态栈（新帧开始时调用）。 */
    fun clear() {
        transformStack.clear()
        alphaStack.clear()
        colorMatrixStack.clear()
        clipStack.clear()
    }

    /** 推入平移缩放变换。 */
    fun pushTransform(tx: Float, ty: Float, scaleX: Float, scaleY: Float) {
        transformStack.addFirst(TransformEntry.TranslateScale(tx, ty, scaleX, scaleY))
    }

    /** 推入 3×3 行优先仿射矩阵变换。 */
    fun pushTransform3x3(mat3x3: FloatArray) {
        transformStack.addFirst(TransformEntry.Matrix3x3(mat3x3))
    }

    /** 推入 4×4 列优先矩阵变换。 */
    fun pushTransform4x4(mat4x4: FloatArray) {
        transformStack.addFirst(TransformEntry.Matrix4x4(mat4x4))
    }

    /** 弹出最近一次变换（三种变换共栈，与拆分前一致）。 */
    fun popTransform() {
        transformStack.removeFirstOrNull()
    }

    /** 推入透明度乘子。 */
    fun pushAlpha(alpha: Float) {
        alphaStack.addFirst(alpha)
    }

    /** 弹出最近一次透明度乘子。 */
    fun popAlpha() {
        alphaStack.removeFirstOrNull()
    }

    /** 推入 4×5 色彩矩阵（防御性复制）。 */
    fun pushColorMatrix(matrix4x5: FloatArray) {
        colorMatrixStack.addFirst(matrix4x5.copyOf())
    }

    /** 弹出最近一次色彩矩阵。 */
    fun popColorMatrix() {
        colorMatrixStack.removeFirstOrNull()
    }

    /** 推入裁剪区域：与当前栈顶有效裁剪求交后入栈。 */
    fun pushClip(x: Float, y: Float, width: Float, height: Float) {
        val parent = clipStack.firstOrNull()
        if (parent == null) {
            clipStack.addFirst(floatArrayOf(x, y, width, height))
        } else {
            clipStack.addFirst(intersectClip(parent, x, y, width, height))
        }
    }

    /** 弹出最近一次裁剪。 */
    fun popClip() {
        clipStack.removeFirstOrNull()
    }

    /**
     * 根据当前内部状态栈创建不可变快照。
     *
     * 栈序约定：快照列表迭代序与拆分前 `ArrayList(java.util.ArrayDeque)` 逐位一致——
     * push 落队头、迭代自队头开始，即列表首元素为最新入栈项（kotlin ArrayDeque.addFirst 同序）。
     */
    fun snapshot(): StateSnapshot {
        val alphas = if (alphaStack.isEmpty()) emptyList() else ArrayList(alphaStack)
        val matrices = if (colorMatrixStack.isEmpty()) emptyList() else ArrayList(colorMatrixStack)
        val clip = clipStack.firstOrNull()
        return StateSnapshot(ArrayList(transformStack), alphas, matrices, clip)
    }

    private companion object {
        /** 两裁剪矩形（[x, y, w, h]）求交；无交叠时返回零尺寸矩形（裁掉一切）。 */
        fun intersectClip(parent: FloatArray, x: Float, y: Float, width: Float, height: Float): FloatArray {
            val left = maxOf(parent[0], x)
            val top = maxOf(parent[1], y)
            val right = minOf(parent[0] + parent[2], x + width)
            val bottom = minOf(parent[1] + parent[3], y + height)
            return floatArrayOf(left, top, maxOf(0f, right - left), maxOf(0f, bottom - top))
        }
    }
}
